/* eslint-disable no-use-before-define */
import React, { useMemo } from 'react';

import { type DefaultRenderActivity, type IActivityRenderer, type IActivityRendererProps } from './types';
import { getIsFromUser } from './utils';

type ActivityRendererManagerProps<T extends IActivityRendererProps> = {
  defaultRenderer: DefaultRenderActivity;
  renderers: Array<IActivityRenderer<T>>;
} & T;

/**
 * `ActivityRendererManager` is a React functional component that manages the rendering of activities.
 * It uses a list of renderers to find the appropriate renderer for a given activity.
 * If no suitable renderer is found, it falls back to a default renderer.
 *
 * @template T - The type of the activity renderer props.
 * @param {ActivityRendererManagerProps<T>} props - The properties for the ActivityRendererManager component.
 * @param {IActivity} props.activity - The activity to be rendered.
 * @param {React.ComponentType<T>} props.defaultRenderer - The default renderer to use if no suitable renderer is found.
 * @param {IActivityRenderer<T>[]} props.renderers - The list of renderers to choose from.
 * @param {RenderActivityParams} props.params - Additional parameters for rendering.
 * @returns {React.ReactNode} The rendered activity.
 */
// @ts-ignore
export const ActivityRendererManager = React.memo(<T extends IActivityRendererProps>(props: ActivityRendererManagerProps<T>) => {
  const {
    activity,
    defaultRenderer,
    renderers,
    params,
  } = props;

  const isFromUser = useMemo(() => getIsFromUser(activity, params.username), [activity.from.name, params.username]);

  // find the first renderer that can render the activity
  const Renderer = renderers.find(renderer => renderer.canRender?.(activity, isFromUser));
  if (Renderer) {
    return (
      // @ts-ignore
      <Renderer
        {...props}
      />
    );
  }

  // could not find a renderer, fallback to the default behavior
  return defaultRenderer(activity as any, params);
}) as <T extends IActivityRendererProps>(props: ActivityRendererManagerProps<T>) => React.ReactNode;
